import { useState } from "react";
import emailjs from '@emailjs/browser';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID!;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID!;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY!;

export function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('idle');

    try {
      // Prepare template parameters that match your EmailJS template
      const templateParams = {
        from_name: formData.firstName,
        last_name: formData.lastName,
        from_email: formData.email,
        phone: formData.phone,
        service_interest: formData.service,
        message: formData.message,
        to_email: 'your-email@example.com' // Replace with your email
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('Email sent successfully:', result);
      setStatus('success');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl mb-4">Tedd meg az első lépéseket</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Nyugodtan írd meg nekem a kérdéseidet. Figyeld az e-mailjeidet, oda
            érkezik majd a válaszom.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl"></CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="firstName">Keresztneved</label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="lastName">Vezetékneved</label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="email">E-mail címed</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone">Telefonszámod</label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="service">Melyik szolgáltatásom érdekel?</label>
                  <select 
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-border rounded-lg bg-white"
                    required
                  >
                    <option value="">Válassz a szolgáltatások közül</option>
                    <option value="YOGIVITY Program">YOGIVITY Program</option>
                    <option value="Nutritional Consultation">Étkezési Tanácsadás</option>
                    <option value="Monthly Online Subscription">Jógázz Online</option>
                    <option value="General Inquiry">Általános Érdeklődés</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message">Üzeneted</label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {/* Status Messages */}
                {status === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-center">
                      ✅ Üzeneted sikeresen elküldve! Hamarosan válaszolok.
                    </p>
                  </div>
                )}
                
                {status === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-center">
                      ❌ Hiba történt az üzenet küldése közben. Próbáld újra vagy írj az email címemre.
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="
                    w-full
                    bg-primary 
                    text-primary-foreground
                    hover:bg-[#6d5a44]
                    active:bg-[#5a4a38]
                    transition-colors 
                    duration-200
                    transform 
                    hover:scale-[1.02]
                    active:scale-[0.98]
                    shadow-md 
                    hover:shadow-lg
                    hover:shadow-primary/30
                    border 
                    border-primary/20
                    hover:border-primary/40
                    disabled:opacity-50
                    disabled:cursor-not-allowed
                  "
                  size="lg"
                >
                  {isLoading ? 'Küldés...' : 'Küldés'}
                </Button>
              </form>

              <p className="text-sm text-muted-foreground text-center">
                Örömmel olvasom és válaszolok minden kérdésedre a jógával és az
                egészséges életmóddal kapcsolatban.
              </p>
            </CardContent>
          </Card>

          <div className="mt-12">
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="text-lg text-center">
                  Vagy írj erre az e-mail címre nekem:
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p
                  className="text-xl font-semibold text-center"
                  style={{ color: "#6b5b47" }}
                >
                  hello@yogivity.com
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}